<script setup>
  import { useI18n } from 'vue-i18n';
  import BaseModal from '@/components/base/BaseModal.vue';
  import Icon from '@/components/icons/Icon.vue';
  import { useWidgetStore } from '@/stores/WidgetStore';
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';

  const widgetStore = useWidgetStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  const showModal = defineModel();

  const { t } = useI18n();

  // The logos are on another origin, where the download attribute is ignored, so fetch the file
  // (the CDN allows it) and save it from a local URL; if that fails, open it instead
  function sendDownloadEvent(file) {
    googleAnalyticsStore.sendEvent('Web_Widget_Logo_Download', {
      File_Name: file
    });
  }

  async function handleDownload(logo) {
    sendDownloadEvent(logo.file);

    try {
      const response = await fetch(logo.url);

      if (!response.ok) throw new Error(response.status);

      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');

      link.href = objectUrl;
      link.download = logo.file;
      link.click();

      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch {
      window.open(logo.url, '_blank', 'noopener');
    }
  }

  function handleDownloadAll() {
    sendDownloadEvent(widgetStore.logoDownloads.zipFile);
  }
</script>

<template>
  <BaseModal v-model="showModal" width="wide" height="tall">
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">{{ t('widgets.logos.title') }}</h2>
    </header>

    <div class="jwx-modal__body">
      <!-- The logos are transparent, so each sits on a checkerboard to show its shape -->
      <ul class="jwx-widget-logos" :aria-label="t('widgets.logos.preview')">
        <li v-for="logo of widgetStore.logoDownloads.logos" :key="logo.file" class="jwx-widget-logos__item">
          <div class="jwx-widget-logos__preview" :data-tone="logo.light ? 'light' : null">
            <img :src="logo.url" :alt="logo.file" class="jwx-widget-logos__image" loading="lazy">
            <button
              @click="handleDownload(logo)"
              type="button"
              class="jwx-widget-logos__download"
              :aria-label="t('widgets.logos.downloadLogo', { file: logo.file })"
            >
              <Icon name="download" class="jwx-widget-logos__download-icon" />
            </button>
          </div>
          <p class="jwx-widget-logos__file">{{ logo.file }}</p>
        </li>
      </ul>
    </div>

    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <a @click="handleDownloadAll" :href="widgetStore.logoDownloads.zipUrl" class="jwx-button jwx-button--success" download>
          {{ t('widgets.logos.downloadZip') }}
        </a>
      </div>
    </div>
  </BaseModal>
</template>
